import { useEffect, useState } from "react";
import { ArrowRight, Feather } from "lucide-react";
import { fetchNewsMap } from "../../lib/newsApi";
import { CanadaTileMap } from "../CanadaTileMap";
import { TonePill } from "../ScoreGauge";

export const MapView = ({ onFilter, onOpenStory }) => {
  const [hours, setHours] = useState(168);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  useEffect(() => { fetchNewsMap(hours).then(setData).catch(() => {}); }, [hours]);
  const p = data?.provinces.find((x) => x.code === selected);
  const indig = data?.provinces.reduce((n, x) => n + x.indigenous, 0) || 0;

  return (
    <div data-testid="map-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl text-slate-900">News map</h2>
          <p className="mt-1 text-sm text-slate-500">Tile colour shows coverage tone; tap a province or territory for its stories, municipalities and top event.</p>
        </div>
        <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="map-hours-select" aria-label="Time range">
          <option value={24}>Last 24 hours</option><option value={168}>Last 7 days</option><option value={720}>Last 30 days</option>
        </select>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="min-w-0 rounded-3xl border border-slate-200 bg-white p-4 sm:p-6">
          {data ? <CanadaTileMap provinces={data.provinces} selected={selected} onSelect={setSelected} /> : <div className="h-64 animate-pulse rounded-xl bg-slate-50" />}
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="map-panel">
          {!p ? (
            <ul className="space-y-1.5 text-sm">
              {data?.provinces.slice().sort((a, b) => b.count - a.count).map((x) => (
                <li key={x.code}><button onClick={() => setSelected(x.code)} className="flex w-full justify-between rounded-lg px-2 py-1 hover:bg-slate-50" data-testid={`map-list-${x.code}`}>
                  <span className="text-slate-700">{x.name}</span><span className="font-mono text-xs text-slate-500">{x.count}</span></button></li>
              ))}
            </ul>
          ) : (
            <div>
              <div className="flex items-center justify-between"><h3 className="font-heading text-xl font-semibold text-slate-900" data-testid="map-panel-title">{p.name}</h3><TonePill score={p.score} /></div>
              <p className="mt-2 text-sm text-slate-600">{p.count} items · {p.mainstream} mainstream · {p.independent} independent · {p.indigenous} Indigenous-affairs</p>
              {p.top_story && <button onClick={() => onOpenStory(p.top_story.id)} className="mt-4 block w-full rounded-xl bg-sky-50 p-3 text-left text-sm" data-testid="map-top-story">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-sky-800">Top story</span><span className="mt-1 block font-medium text-slate-900">{p.top_story.label}</span></button>}
              <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Municipalities in the news</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {p.top_cities.map((c) => <button key={c.city} onClick={() => onFilter({ province: p.code, city: c.city })} className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-700 hover:border-sky-300" data-testid={`map-city-${c.city}`}>{c.city} · {c.count}</button>)}
                {p.top_cities.length === 0 && <span className="text-xs text-slate-400">None detected</span>}
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <button onClick={() => onFilter({ province: p.code })} className="inline-flex items-center gap-1 rounded-full bg-sky-900 px-4 py-2 text-sm text-white" data-testid="map-open-feed-btn">Open {p.code} feed <ArrowRight size={14} /></button>
                <button onClick={() => setSelected(null)} className="text-sm text-slate-500 underline" data-testid="map-clear-btn">All regions</button>
              </div>
            </div>
          )}
        </div>
      </div>
      <button onClick={() => onFilter({ indigenous: true })} className="mt-6 flex w-full items-center gap-4 rounded-3xl border border-amber-200 bg-amber-50 p-5 text-left" data-testid="map-indigenous-btn">
        <Feather className="shrink-0 text-amber-700" />
        <span className="flex-1"><span className="block font-heading font-semibold text-slate-900">Indigenous community coverage</span>
          <span className="text-sm text-slate-600">{indig} items on Indigenous affairs across regions — including IndigiNews and Indigenous-focused reporting from all outlets.</span></span>
        <ArrowRight size={18} className="text-amber-700" />
      </button>
    </div>
  );
};
